import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, Bot, Send } from 'lucide-react';
import pic from '../../assets/images/pic.png';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { useConversationsApi, MessageDTO } from '../../hooks/useConversationsApi';
import { useDocumentsApi } from '../../hooks/useDocumentsApi';
import { useAuth } from '../../features/auth/contexts/AuthContext';
import { MarkdownRenderer } from '../MarkdownRenderer';
import { getAccessToken } from '../../lib/auth-tokens';

const BASE_URL = import.meta.env.VITE_BFF_URL || 'http://localhost:3010';

interface AiChatAssistantProps {
  conversationId?: string;
  documentId?: string;
  pageSize?: number;
}

export const AiChatAssistant: React.FC<AiChatAssistantProps> = ({
  conversationId,
  documentId,
  pageSize = 10,
}) => {
  const { getConversation, getMessages } = useConversationsApi();
  const { logout } = useAuth();
  const documentsApi = useDocumentsApi();

  const [messages, setMessages] = useState<MessageDTO[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingOlder, setLoadingOlder] = useState<boolean>(false);
  const [isThinking, setIsThinking] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [inputValue, setInputValue] = useState('');
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [currentConversationId, setCurrentConversationId] = useState<string | undefined>(
    conversationId,
  );

  // When the selected document changes, try to load its existing conversation ID
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        // Reset messages for a fresh view
        setMessages([]);
        setHasMore(false);
        if (!documentId) {
          setCurrentConversationId(undefined);
          return;
        }
        const d: any = await documentsApi.get(documentId);
        if (!mounted) return;
        const convId = d?.externalEntityReferenceId as string | undefined;
        setCurrentConversationId(convId || undefined);
      } catch (_err) {
        if (!mounted) return;
        setCurrentConversationId(undefined);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [documentId]);

  // Load conversation + latest messages
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      // Do not surface errors for conversation preloading; show empty state instead
      setError(null);
      try {
        if (!currentConversationId) {
          // If we don't know the conversation yet, we can't load messages.
          setMessages([]);
          setHasMore(false);
          return;
        }
        await getConversation(currentConversationId);
        if (!mounted) return;

        const page = await getMessages(currentConversationId, {
          limit: pageSize,
        });
        if (!mounted) return;
        const sorted = [...page.messages].sort(
          (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
        setMessages(sorted);
        setHasMore(page.hasMore);
      } catch (e: any) {
        if (!mounted) return;
        // Silent fallback: clear any previous content and proceed with empty chat UI
        setMessages([]);
        setHasMore(false);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [currentConversationId, getConversation, getMessages, pageSize]);

  useEffect(() => {
    if (chatContainerRef.current) {
      // For flex-col-reverse, scrolling to the top shows latest at the bottom visually
      chatContainerRef.current.scrollTop = 0;
    }
  }, [messages, isThinking]);

  const loadOlder = useCallback(async () => {
    if (!messages.length || loadingOlder || !hasMore || !currentConversationId) return;
    setLoadingOlder(true);
    try {
      const firstId = messages[0]?.id;
      const page = await getMessages(currentConversationId, {
        limit: pageSize,
        beforeMessageId: firstId,
      });
      const sorted = [...page.messages].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
      setMessages((prev) => [...sorted, ...prev]);
      setHasMore(page.hasMore);
    } catch (e: any) {
      setError(e?.message || 'Échec du chargement des anciens messages');
    } finally {
      setLoadingOlder(false);
    }
  }, [messages, loadingOlder, hasMore, currentConversationId, getMessages, pageSize]);

  // Sentinel ref for infinite scroll — triggers loadOlder when scrolled to top
  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingOlder) {
          loadOlder();
        }
      },
      { root: chatContainerRef.current, threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingOlder, loadOlder]);

  const handleUnauthorized = async () => {
    try {
      await logout();
    } finally {
      window.location.replace('/login');
    }
  };

  // Send message through the document Ask endpoint, which also returns AI response
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!documentId || !inputValue.trim()) return;
    setSending(true);
    setIsThinking(true);
    setError(null);

    // 1) Optimistically append the user's message so it's visible immediately
    const optimisticId = `temp-${Date.now()}`;
    const optimisticMessage: MessageDTO = {
      id: optimisticId,
      conversationId: currentConversationId || 'temp',
      senderExternalRef: 'me',
      body: inputValue.trim(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimisticMessage]);

    // Clear the input immediately for better UX
    const submittedText = inputValue.trim();
    setInputValue('');
    try {
      const authHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
      const accessToken = getAccessToken();
      if (accessToken) {
        authHeaders['Authorization'] = `Bearer ${accessToken}`;
      }
      const res = await fetch(`${BASE_URL}/documents/${documentId}/ask`, {
        method: 'POST',
        headers: authHeaders,
        credentials: 'include',
        body: JSON.stringify({ message: submittedText }),
      });

      if (res.status === 401 || res.status === 403) {
        await handleUnauthorized();
        return;
      }
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`Échec de l'envoi: ${res.status} ${txt}`);
      }

      const data = (await res.json()) as {
        documentId: string;
        conversationId: string;
        messages: {
          id: string;
          body: string;
          sender: string;
          createdAt: string;
        }[];
      };

      // Update current conversation ID from response
      setCurrentConversationId(data.conversationId);

      // Map returned messages to MessageDTO for display
      const mapped: MessageDTO[] = data.messages.map((m) => ({
        id: m.id,
        conversationId: data.conversationId,
        senderExternalRef: m.sender,
        body: m.body,
        createdAt: m.createdAt,
        updatedAt: m.createdAt,
      }));
      // Ensure ascending chronological order
      mapped.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
      // 2) Append server-confirmed messages, replacing the optimistic one and deduplicating
      setMessages((prev) => {
        // Remove the optimistic message
        const withoutOptimistic = prev.filter((m) => m.id !== optimisticId);
        // Merge new messages, avoiding duplicates by id
        const existingIds = new Set(withoutOptimistic.map((m) => m.id));
        const newMsgs = mapped.filter((m) => !existingIds.has(m.id));
        const merged = [...withoutOptimistic, ...newMsgs];
        merged.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        return merged;
      });
    } catch (err: any) {
      // Remove the optimistic message on failure and keep UI clean without surfacing loud errors
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      setError(null);
    } finally {
      setSending(false);
      setIsThinking(false);
    }
  };

  return (
    <Card className="h-full flex flex-col bg-white rounded-lg shadow-sm">
      <div className="flex items-center p-4 border-b flex-shrink-0">
        <MessageCircle className="h-6 w-6 text-primary mr-3" />
        <h2 className="text-lg font-semibold text-gray-800">Assistant IA</h2>
      </div>

      <div
        ref={chatContainerRef}
        className="flex-grow overflow-y-auto flex flex-col-reverse p-6 space-y-4 space-y-reverse"
      >
        {isThinking && (
          <div className="flex items-end gap-3 justify-start">
            <div className="h-6 w-6 flex items-center justify-center rounded-md bg-indigo-50 text-indigo-600 border border-indigo-100 flex-shrink-0">
              <Bot className="h-4 w-4" />
            </div>
            <div className="max-w-[75%] p-3 rounded-2xl bg-gray-100 text-gray-800 rounded-bl-none">
              <div className="flex items-center space-x-1">
                <span className="h-2 w-2 bg-primary rounded-full animate-pulse [animation-delay:-0.3s]"></span>
                <span className="h-2 w-2 bg-primary rounded-full animate-pulse [animation-delay:-0.15s]"></span>
                <span className="h-2 w-2 bg-primary rounded-full animate-pulse"></span>
              </div>
            </div>
          </div>
        )}
        {loading && <div>Chargement…</div>}
        {error && !loading && <div className="text-red-600">{error}</div>}
        {!loading && !error && messages.length === 0 && (
          <div className="flex items-end gap-3 justify-start">
            <Bot className="h-6 w-6 text-primary flex-shrink-0" />
            <div className="max-w-[75%] p-3 rounded-2xl bg-gray-100 text-gray-800 rounded-bl-none">
              <p className="text-sm">Comment puis-je vous aider aujourd'hui ?</p>
              <p className="text-xs text-right mt-1 text-gray-400">
                {new Date().toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>
          </div>
        )}
        {!loading &&
          !error &&
          [...messages].reverse().map((m) => {
            const isAi = m.senderExternalRef?.startsWith('zuroq-');
            return (
              <div
                key={m.id}
                className={`flex items-end gap-3 ${isAi ? 'justify-start' : 'justify-end'}`}
              >
                {isAi && (
                  <div className="h-6 w-6 flex items-center justify-center rounded-md bg-indigo-50 text-indigo-600 border border-indigo-100 flex-shrink-0">
                    <Bot className="h-4 w-4" />
                  </div>
                )}
                <div
                  className={`max-w-[75%] p-3 rounded-2xl ${isAi ? 'bg-gray-100 text-gray-800 rounded-bl-none' : 'bg-primary text-primary-foreground rounded-br-none'}`}
                >
                  {isAi ? (
                    <div className="text-sm">
                      <MarkdownRenderer content={m.body} />
                    </div>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                  )}
                  <p
                    className={`text-xs text-right mt-1 ${isAi ? 'text-gray-400' : 'text-blue-200'}`}
                  >
                    {new Date(m.createdAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
                {!isAi && (
                  <img
                    src={pic}
                    alt="User avatar"
                    className="h-6 w-6 rounded-full object-cover flex-shrink-0 border border-slate-200"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).src = '/logo512.png';
                    }}
                  />
                )}
              </div>
            );
          })}
        {/* Scroll sentinel — sits at the visual top (oldest messages) in flex-col-reverse */}
        {hasMore && !loading && (
          <div ref={sentinelRef} className="flex justify-center py-2">
            {loadingOlder && <span className="text-xs text-gray-400">Chargement…</span>}
          </div>
        )}
      </div>

      <div className="p-4 border-t bg-white flex-shrink-0">
        <form onSubmit={handleSubmit} className="flex items-center gap-2">
          <Input
            placeholder={
              documentId ? 'Posez une question...' : 'Sélectionnez un document pour chatter'
            }
            className="flex-grow bg-white border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            disabled={!documentId || sending}
          />
          <Button
            type="submit"
            className="bg-primary hover:bg-primary/90 rounded-md w-10 h-10 flex-shrink-0"
            disabled={!documentId || sending}
          >
            <Send className="h-5 w-5 text-white" />
          </Button>
        </form>
      </div>
    </Card>
  );
};
